import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  Breadcrumbs,
  ChevronRightOutlinedIcon,
  FolderOutlinedIcon,
  Search,
  SearchAltIcon,
  truncateTextWithEllipsis,
} from '@utilities';

import Answers from '../Answers';
import AttributeOptionsResults from '../AttributeOptionResults';

import styles from './_index.module.scss';

const GroupedPromptOptions = ({
  activeAttribute,
  activeGroupedPrompt,
  activePrompt,
  activePromptAnswers,
  isLoading,
  onAnswerChange,
  results,
  searchQuery,
  selectedAnswers,
  setActiveAttribute,
  setActiveGroupedPrompt,
  setSearchQuery,
}) => {
  const renderCards = (list) =>
    list?.map((item, index) => {
      const isActive = activeAttribute?.id === item?.id;
      const displayName = truncateTextWithEllipsis({
        length: 70,
        text: item?.name,
      });

      return (
        <div
          className={classNames(styles['attribute-card'], {
            [styles['is-active']]: isActive,
          })}
          key={item?.id ?? index}
          onClick={() => {
            setActiveAttribute(item);
          }}
          role="button"
          tabIndex={0}
          title={item?.name}
        >
          <FolderOutlinedIcon />
          <p>{displayName}</p>
          <ChevronRightOutlinedIcon />
        </div>
      );
    });

  const renderGroupCards = (groups) =>
    groups?.map((group, index) => {
      const isActive = activeGroupedPrompt?.name === group?.name;
      const groupName = truncateTextWithEllipsis({
        length: 70,
        text: group?.name,
      });

      return (
        <div
          className={classNames(styles['attribute-card'], {
            [styles['is-active']]: isActive,
          })}
          key={group?.name ?? index}
          onClick={() => {
            setActiveGroupedPrompt(group);
          }}
          role="button"
          tabIndex={0}
          title={group?.name}
        >
          <FolderOutlinedIcon />
          <p>{groupName}</p>
          <ChevronRightOutlinedIcon />
        </div>
      );
    });

  let breadcrumbs = [
    {
      label: activePrompt?.name,
      onClick: () => {
        setActiveAttribute(null);
        setActiveGroupedPrompt(null);
        setSearchQuery('');
      },
    },
  ];

  if (activeGroupedPrompt) {
    breadcrumbs.push({
      label: activeGroupedPrompt?.name,
      onClick: () => {
        setActiveAttribute(null);
        setSearchQuery('');
      },
    });
  }

  if (activeAttribute && activeGroupedPrompt?.attributes?.length > 1) {
    breadcrumbs.push({ label: activeAttribute?.name });
  }

  return (
    <>
      <Search
        className={styles['grouped-prompt-result-search']}
        icon={<SearchAltIcon />}
        onChange={setSearchQuery}
        placeholder={`Filter ${activeGroupedPrompt?.name || activePrompt?.name}`}
        value={searchQuery}
      />
      <Breadcrumbs options={breadcrumbs} />
      {searchQuery ? (
        <AttributeOptionsResults
          activeGroupedPrompt={activeGroupedPrompt}
          activePrompt={activePrompt}
          isLoading={isLoading}
          onAnswerChange={onAnswerChange}
          results={results}
          selectedAnswers={selectedAnswers}
        />
      ) : (
        <div className={styles['prompt-results']}>
          {!activeAttribute ? (
            <div className={styles['attributes']}>
              {activeGroupedPrompt
                ? renderCards(activeGroupedPrompt?.attributes)
                : renderGroupCards(activePrompt?.groupedPrompts)}
            </div>
          ) : (
            <Answers
              activeGroupedPrompt={activeGroupedPrompt}
              activePrompt={activePrompt}
              activePromptAnswers={activePromptAnswers}
              isLoading={isLoading}
              onAnswerChange={onAnswerChange}
              results={results}
            />
          )}
        </div>
      )}
    </>
  );
};

GroupedPromptOptions.propTypes = {
  activeAttribute: PropTypes.object,
  activeGroupedPrompt: PropTypes.object,
  activePrompt: PropTypes.object.isRequired,
  activePromptAnswers: PropTypes.array,
  isLoading: PropTypes.bool,
  onAnswerChange: PropTypes.func,
  results: PropTypes.array,
  searchQuery: PropTypes.string,
  selectedAnswers: PropTypes.object,
  setActiveAttribute: PropTypes.func.isRequired,
  setActiveGroupedPrompt: PropTypes.func.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
};

export default GroupedPromptOptions;
